import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Badge,
  Pagination,
  Avatar,
  Stack,
  Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { ShoppingCart, Chat } from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBuyerConnections } from "../redux/slices/buyerNetworkSlice";
import { getCart } from "../redux/slices/cartSlice";
import SellerChatModal from "../Components/SellerChatModal";
import PageTitle from "../Components/PageTitle";

export default function SellerNetwork() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const { buyers, loading, error, totalPages } = useSelector(
    (state) => state.buyerNetwork,
  );
  const { cart } = useSelector((state) => state.cart);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTerm, setSearchTerm] = useState("");
  const [cartCounts, setCartCounts] = useState({});
  const { user } = useSelector((state) => state.user);
  const userId = user._id;

  // Chat modal state
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSeller, setActiveSeller] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  // Fetch connected sellers when page loads
  useEffect(() => {
    dispatch(fetchBuyerConnections({ page, limit: itemsPerPage }));
  }, [dispatch, page]);

  // Fetch cart for all sellers once user logs in
  useEffect(() => {
    if (!buyers?.length || !userId) return;

    const fetchCartsSequentially = async () => {
      for (const seller of buyers.filter((b) => b.status === "Accepted")) {
        const sellerId = seller?.connectionData?.otherUser?._id;
        const buyerCategoryId = seller?.connectionData?.buyerCategory?._id;

        if (!sellerId || !buyerCategoryId) continue;

        try {
          const res = await dispatch(
            getCart({
              userId,
              seller: sellerId,
              buyerCategories: [buyerCategoryId],
            }),
          ).unwrap();

          const count = Array.isArray(res)
            ? res.length
            : res?.items?.length || 0;

          setCartCounts((prev) => ({
            ...prev,
            [sellerId]: count,
          }));

          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (err) {
          setCartCounts((prev) => ({
            ...prev,
            [sellerId]: 0,
          }));
        }
      }
    };

    fetchCartsSequentially();
  }, [buyers, userId, dispatch]);

  // Filters
  const filteredConnectedSellers = buyers
    .filter((b) => b.status === "Accepted")
    .filter(
      (b) =>
        b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.city?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOpenChat = (seller) => {
    setActiveSeller(seller);
    setChatOpen(true);
  };

  return (
 <>
 <PageTitle title="Seller Network - BizBridge" 
    description="Explore and manage your network of connected sellers"
    keywords="seller network, connected sellers, buyer-seller connections"
    url="/buyer-dashboard/seller-network"
    />
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "#f8fafc",
        py: { xs: 1, sm: 2 }, 
      }}
    > 
      {/* Breadcrumb */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          width: "fit-content",
          backgroundColor: "#fff",
          ml: { xs: 1, sm: 2.9 },
          padding: "13px",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/buyer-dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
          Seller Network
        </Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2} alignItems="center" mb={3}>
          {/* Page Title */}
          <Grid item xs={12} md={6}>
            <Typography fontWeight={700} fontSize={22}>
              Seller Network
            </Typography>
          </Grid>

          {/* Search (Right aligned) */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <TextField
              placeholder="Search sellers..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: { xs: "100%", sm: "300px" },
                background: "#fff",
                borderRadius: 2,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#8b99a8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => setSearchTerm("")}
                      sx={{ minWidth: 0, color: "#8b99a8", fontSize: 14 }}
                    >
                      ✕
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Loader or Error */}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={10}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" textAlign="center">
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <>
            {/* Connected Sellers */}
            {filteredConnectedSellers?.length > 0 ? (
              <Grid container spacing={isMobile ? 2 : 4} sx={{ mb: 4 }}>
                {filteredConnectedSellers?.map((seller) => (
                  <Grid item xs={12} sm={12} md={6} lg={4} key={seller._id}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        background: "#fff",
                        boxShadow: "0px 6px 18px rgba(0,0,0,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        // height: "100%",
                      }}
                    >
                      {/* Avatar - Centered at top */}
                      <Avatar
                        alt={seller.name}
                        src={seller.profileImage}
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: "#ededed",
                          fontWeight: 700,
                          fontSize: 24,
                          color: "#22364a",
                          mb: 1,
                        }}
                      >
                        {seller.profileImage || seller.name?.charAt(0)}
                      </Avatar>

                      {/* Seller Info - Centered below avatar */}
                      <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography
                          fontWeight={700}
                          fontSize={18}
                          color="#22364a"
                          sx={{ mb: 0.5 }}
                        >
                          {seller.name}
                        </Typography>
                        <Typography fontSize={14} color="#8b99a8">
                          {seller.company || seller.city || "Seller"}
                        </Typography>
                      </Box>

                      {/* Action Buttons Section */}
                      <Box sx={{ width: "100%" }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 2 }}
                        >
                          <Button
                            variant="contained"
                            fullWidth
                            sx={{
                              background: "#0b4064",
                              fontWeight: 700,
                              textTransform: "none",
                              borderRadius: 2,
                              fontSize: 15,
                              boxShadow: 0,
                              py: 1,
                              "&:hover": { background: "#0b4064" },
                            }}
                            onClick={() =>
                              navigate("/buyer-dashboard/view-products", {
                                state: {
                                  connectionId: seller.connectionData?._id,
                                  otherUserId:
                                    seller.connectionData?.otherUser?._id,
                                },
                              })
                            }
                          >
                            View Products
                          </Button>

                          {/* Cart Icon with Count */}
                          <Badge
                            badgeContent={
                              cartCounts[
                                seller?.connectionData?.otherUser?._id
                              ] !== undefined
                                ? cartCounts[
                                    seller?.connectionData?.otherUser?._id
                                  ]
                                : "?"
                            }
                            color="primary"
                            sx={{ cursor: "pointer" }}
                          >
                            <IconButton
                              onClick={() => {
                                navigate("/buyer-dashboard/cart", {
                                  state: {
                                    userId,
                                    sellerIds: [
                                      seller?.connectionData?.otherUser?._id,
                                    ],
                                    buyerCategories: [
                                      seller?.connectionData?.buyerCategory
                                        ?._id,
                                    ],
                                  },
                                });
                              }}
                              sx={{
                                border: "1px solid #e0e0e0",
                                width: 48,
                                height: 48,
                              }}
                            >
                              <ShoppingCart sx={{ color: "#22364a" }} />
                            </IconButton>
                          </Badge>
                        </Stack>

                        {/* Pending Invoices + Message Button Row */}
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ width: "100%" }}
                        >
                          <Button
                            fullWidth
                            variant="contained"
                            sx={{
                              bgcolor: "#eef2ff",
                              color: "#0b4064",
                              fontWeight: 600,
                              boxShadow: "none",
                              "&:hover": { bgcolor: "#e0e7ff" },
                              textTransform: "none",
                              fontSize: 14,
                              py: 1,
                              borderRadius: 2,
                            }}
                            onClick={() =>
                              navigate(
                                "/buyer-dashboard/seller-pending-invoices",
                                {
                                  state: {
                                    buyerId:
                                      seller?.connectionData?.otherUser?._id,
                                  },
                                },
                              )
                            }
                          >
                            Pending Invoices
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              textTransform: "none",
                              fontSize: 14,
                              py: 1,
                              borderColor: "#d1d5db",
                              color: "#374151",
                              borderRadius: 2,
                              "&:hover": {
                                borderColor: "#9ca3af",
                                backgroundColor: "#f9fafb",
                              },
                            }}
                            startIcon={<Chat sx={{ fontSize: 18 }} />}
                            onClick={() => handleOpenChat(seller)}
                          >
                            Message
                          </Button>
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography textAlign="center" color="#8b99a8" py={3}>
                No connected sellers found.
              </Typography>
            )}
          </>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 10,
              pb: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Chat Modal */}
      <SellerChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        seller={activeSeller}
        currentUser={user?._id}
        currentMode="buyer"
        onlineUsers={onlineUsers}
      />
    </Box>
 </>
  );
}
